import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import { useAsync } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { useField } from 'formik';

import { TableProps } from 'components/Table';
import { FormValues, Q1Value } from 'models/documents';
import { SubjectDetailColumns } from 'models/response';

import utils from 'utils';
import service from 'services';
import actions from 'state/actions';
import selectors from 'state/selectors';

import Modal from 'components/Modal';
import Table from 'components/Table';
import ErrorBoundary from 'components/ErrorBoundary';
import Checkbox from 'components/Buttons/CheckboxButton';
import Dropdown from 'components/Input/dropdown/Dropdown';
import CommonButton from 'components/Buttons/CommonButton';

export const CheckboxWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const TableContainer = styled.div`
  max-width: 600px;
`;

export const SubTitle = styled.div`
  margin: 10px 0px;
  font-size: 20px;
  color: ${({ theme }) => theme.colorPalette.grey.grey7};
`;

export const InputName = styled.div`
  font-size: 12px;
  margin: 5px 0px;
`;

export const Content = styled.div`
  display: flex;
`;

const defaultColumns = [
  {
    Header: 'supplierCode',
    accessor: 'supplierCode',
    width: 100,
  },
];

interface Option {
  value: string;
  label: string;
}

interface PopulateQ1 {
  title: string;
  options: Option[];
  q1FixedName: string;
}

const SubjectDetails = () => {
  const t = utils.intl.useTranslator();
  const [tableColumnsResponse, setTableColumnsResponse] = useState<SubjectDetailColumns[]>([]);
  const [tableColumns, setTableColumns] = useState<TableProps['columns']>([]);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [supplierCode, , helpers] = useField<FormValues['supplierCode']>('supplierCode');
  const [temporarySupplierCode, setTemporarySupplierCode] = useState(supplierCode.value);
  const [viewEntireTable, setViewEntireTable] = useState<boolean>(false);
  const [infoFillQ1, setInfoFillQ1] = useState<PopulateQ1 | null>(null);
  const [optionQ1Selected, setOptionQ1Selected] = useState<string>('');
  const dispatch = useDispatch();
  const modalProps = useSelector(selectors.modal.getModalProps);

  const idSubject = useSelector(selectors.documents.selectIdSubject);
  const documentHeader = useSelector(selectors.documents.selectDocumentHeader);

  const [field, , helpersField] = useField<Q1Value>(infoFillQ1?.q1FixedName || '');

  const openSelectFillQ1 = (title: string, content: string, q1FixedName: string) => {
    const splitted = content.split('|');
    const options = splitted.map((el) => {
      return { label: el, value: el };
    });
    setInfoFillQ1({
      title,
      options,
      q1FixedName,
    });
  };

  const updateColumns = useCallback(
    (columns: SubjectDetailColumns[]) => {
      setTableColumns([
        ...defaultColumns,
        ...columns.map(({ title, position, width, q1FixedName }, i) => ({
          Header: title,
          accessor: `${position}`,
          id: `${position}`,
          width,
          Cell: ({ value }: { value: any }) =>
            q1FixedName && q1FixedName.length > 0 ? (
              <span onClick={() => openSelectFillQ1(title, value, q1FixedName)}>{value}</span>
            ) : (
              value ?? ''
            ),
          isVisible: viewEntireTable ? true : i < 5,
        })),
      ]);
    },
    [viewEntireTable],
  );

  useAsync(async () => {
    try {
      if (idSubject) {
        const { data: rows } = await service.getSubjectDetail(idSubject);
        const { data: columns } = await service.getSubjectDetailColumns(idSubject);
        setTableColumnsResponse(columns.sort((a, b) => a.position - b.position));
        const dataRows = Object.values(rows);
        const arrayRows = dataRows.map((ele) => Object.values(ele));
        const objectRows = arrayRows.map((ele) =>
          ele.reduce(
            // @ts-ignore
            (acc, { position, content, supplierCode }) => ({ ...acc, [position]: content, supplierCode }),
            {},
          ),
        );
        updateColumns(columns);
        setTableRows(objectRows);
      }
    } catch (err) {
      console.error(err);
      dispatch(actions.modal.closeModal());
    }
  }, []);

  useEffect(() => {
    updateColumns(tableColumnsResponse);
  }, [updateColumns, tableColumnsResponse]);

  const onConfirm = async () => {
    try {
      helpers.setValue(temporarySupplierCode);
    } catch (e) {
      console.error(e);
    }
  };

  const onSelection = (rows: any[]) => {
    if (rows.length) {
      setTemporarySupplierCode(rows[0]?.supplierCode || '');
    }
  };

  const setQ1Input = () => {
    if (field.value) {
      helpersField.setValue({
        ...field.value,
        content: [{ ...field.value.content[0], content: optionQ1Selected }],
      });
    }
  };

  return (
    <ErrorBoundary>
      <>
        <Modal.Header title={t('subject-details-title')} subtitle={t('subject-details-subtitle')} />
        <Modal.Content>
          <TableContainer>
            <Table
              id="subject-details-table"
              rowId="supplierCode"
              hasSelection={
                !!modalProps.isReadOnly || !documentHeader?.readOnlyHeader?.find((e) => e === 'availableSubjectDetail')
              }
              columns={tableColumns}
              rows={tableRows}
              onSelection={onSelection}
              hasFilter
              hasSort
              hasToolbar
              resetFiltersDependency
            />
          </TableContainer>
        </Modal.Content>
        <CheckboxWrapper>
          <Checkbox
            name="viewEntireTable"
            isActive={viewEntireTable}
            action={(e) => setViewEntireTable(e)}
            label={t('view-entire-table')}
            isEditable={tableColumnsResponse.length > 5}
          />
        </CheckboxWrapper>
        {infoFillQ1 && (
          <>
            <SubTitle>{t('apply-to-q1')}</SubTitle>
            <InputName>{infoFillQ1.title}</InputName>
            <Content>
              <Dropdown
                id="q1-dropdown"
                onChange={(option) => setOptionQ1Selected(option.value)}
                name="format"
                options={infoFillQ1.options}
              />
              <CommonButton
                id="apply-btn"
                action={setQ1Input}
                scope="tertiary"
                value={t('apply')}
                disabled={!optionQ1Selected}
              />
            </Content>
          </>
        )}
        <Modal.Footer
          confirmAction={onConfirm}
          confirmText={t('change-subject-details')}
          confirmDisabled={
            !supplierCode ||
            modalProps.isReadOnly ||
            !!documentHeader?.readOnlyHeader?.find((e) => e === 'availableSubjectDetail')
          }
        />
      </>
    </ErrorBoundary>
  );
};

export default SubjectDetails;
